/**
 * 주문서 URL 구성
 * /order/checkout/mall/12345678-1234-1234-1234-12345 1234567
 * /{OrderSheetCase}/{OrderPhase}/{OrderChannel}/{OrderSheetId}
 */

// 주문서 최상위 카테고리. 주문서 타입
export const OrderSheetCase = {
    /** 주문형 주문서 */
    ORDER: 'order',
    /** 나중에 결제 */
    PAY_NEXT: 'payNext',
    /** 결제수단 주문서 */
    PAYMENT_METHODS: 'paymentMethods',
    /** 결제형 */
    PAYMENT: 'payment',
    /** 디지털 컨텐츠 **/
    DICON_BUYING: 'diconBuying',
} as const

export type OrderSheetCaseType = Constant<typeof OrderSheetCase>

/** 주문 채널 : url 기반 채널 구분에 사용합니다. */
export const OrderChannel = {
    MALL: 'mall',
    EASY_BOOKING: 'easybooking',
    LOCAL_PAY: 'localpay',
    CAFE: 'cafe',
    BLOG: 'blog',
    SELLER: 'seller',
    SUBSCRIPTION: 'subscription',
    FLEA_MARKET: 'fleamarket',
} as const

export type OrderChannelType = Constant<typeof OrderChannel>

/** 주문서 진행 상태 : 주문서의 상태 구분에 사용합니다. (주문서 | 주문완료 | 주문상세) */
export const OrderPhase = {
    CHECKOUT: 'CHECKOUT',
    RESULT: 'RESULT',
    DETAIL: 'DETAIL',
} as const

export type OrderPhaseType = Constant<typeof OrderPhase>

/** 충전 계좌 현재 상태 */
export const AccountStatus = {
    AVAILABLE: 'available',
    MAINTENANCE: 'maintenance',
} as const

/** 알람 수신 타입 */
export const Alarm = {
    NAVERME: 'NAVERME',
    SMS: 'SMS',
} as const

export type AlarmType = Constant<typeof Alarm>

/** 주문서 버전 정보 */
export const APIVersion = {
    V10: 'API_VERSION_10',
    V20: 'API_VERSION_20',
} as const

export type APIVersionType = Constant<typeof APIVersion>

/** 카드결제 개발 카드 타입 (BE) */
export const CardPaymentCard = {
    /** 신용카드 */
    CREDIT: 'crd',
    /** 체크카드 */
    CHECK: 'chk',
    /** 선불카드 */
    PREPAID: 'pre',
    /** NFC 선불카드 */
    NFC_PREPAID: 'nfc_pre',
} as const

export type CardPaymentCardType = Constant<typeof CardPaymentCard>

/** 카드 타입 (FE) */
export const Card = {
    CHECK: 'CHECK',
    CREDIT: 'CREDIT',
    PREPAID: 'PREPAID',
    NFC_PREPAID: 'NFC_PREPAID',
    NONE: 'NONE',
} as const

export const mapCardTypeToCardType: Record<CardPaymentCardType, CardType> = {
    [CardPaymentCard.CHECK]: Card.CHECK,
    [CardPaymentCard.CREDIT]: Card.CREDIT,
    [CardPaymentCard.PREPAID]: Card.PREPAID,
    [CardPaymentCard.NFC_PREPAID]: Card.NFC_PREPAID,
} as const

export type CardType = Constant<typeof Card>

/** 간편카드 > SwipeableItem의 item 타입 */
export const EasyCardItemType = {
    /** 결제 가능한 유효 간편 카드 */
    CARD: 'CARD',
    /** 카드타입 배너 */
    BANNER: 'BANNER',
    /** 새로운 카드 추가 */
    ADD_CARD: 'ADD_CARD',
    /** 결제수단 전체보기 플레이트 */
    FULL_VIEW: 'FULL_VIEW',
} as const

export type EasyCardItemTypeType = Constant<typeof EasyCardItemType>

/** 쿠폰 할인 종류 */
export const CouponDiscount = {
    /** 배송비 할인 */
    DELIVERY_FEE_DISCOUNT: 'DELIVERY_FEE_DISCOUNT',
    /** 중복할인 */
    DUPLICATE_PRODUCT_DISCOUNT: 'DUPLICATE_PRODUCT_DISCOUNT',
    /** 상품할인(네이버페이) C1 */
    MANAGER_PRODUCT_DISCOUNT: 'MANAGER_PRODUCT_DISCOUNT',
    /** 상품할인(판매자) C1 */
    MERCHANT_PRODUCT_DISCOUNT: 'MERCHANT_PRODUCT_DISCOUNT',
    /** 즉시 할인 (판매자) C2 */
    SELLER_IMMEDIATE_DISCOUNT: 'SELLER_IMMEDIATE_DISCOUNT',
    /**
     * 즉시 할인 C2
     * @Deprecated
     **/
    IMMEDIATE_DISCOUNT: 'IMMEDIATE_DISCOUNT',
    /** 복수 할인 C2 */
    MULTI_PURCHASE_DISCOUNT: 'MULTI_PURCHASE_DISCOUNT',
    /** 상품 할인 C2 */
    PRODUCT_DISCOUNT: 'PRODUCT_DISCOUNT',
    /** 스토어 할인 C2 */
    STORE_DISCOUNT: 'STORE_DISCOUNT',
    /** 정기구독 할인 */
    SUBSCRIPTION_DISCOUNT: 'SUBSCRIPTION_DISCOUNT',
} as const

export type CouponDiscountType = Constant<typeof CouponDiscount>

/** 쿠폰 제공자 */
export const CouponProvider = {
    /** 네이버페이 */
    MANAGER: 'MANAGER',
    /** 판매자 C1 */
    MERCHANT: 'MERCHANT',
    /** 판매자 C2 */
    SELLER: 'SELLER',
} as const

export type CouponProviderType = Constant<typeof CouponProvider>

/** 복수구매 할인 유형 */
export const MultiPurchaseUnit = {
    /** 수량 */
    PURCHASE_COUNT: 'PURCHASE_COUNT',
    /** 총액 */
    PURCHASE_AMT: 'PURCHASE_AMT',
} as const

export type MultiPurchaseUnitType = Constant<typeof MultiPurchaseUnit>

/** 배송비 부과 방식 */
export const DeliveryFeeClass = {
    CHARGE: 'CHARGE',
    // TODO:API 응답모델에는 'CHARGE_BY_QUANTITY'이 없음. 확인 요망.
    CHARGE_BY_QUANTITY: 'CHARGE_BY_QUANTITY',
    CONDITIONAL_FREE: 'CONDITIONAL_FREE',
    FREE: 'FREE',
    SECTION_BY_QUANTITY: 'SECTION_BY_QUANTITY',
    REPEAT_BY_QUANTITY: 'REPEAT_BY_QUANTITY',
} as const

export type DeliveryFeeClassType = Constant<typeof DeliveryFeeClass>

export const ExposureArea = {
    CARD: 'CARD',
    BANK: 'BANK',
    VIRTUAL_ACCOUNT: 'VIRTUAL_ACCOUNT',
    MOBILE: 'MOBILE',
    CHARGE_POINT: 'CHARGE_POINT',
    CARD_EASY_PAY: 'CARD_EASY_PAY',
    BANK_EASY_PAY: 'BANK_EASY_PAY',
    SAMSUNG_PAY: 'SAMSUNG_PAY',
    APPLE_PAY: 'APPLE_PAY',
} as const

export type ExposureAreaType = Constant<typeof ExposureArea>

/**
 * 결제 수단

 * @ref https://oss.fin.navercorp.com/payOrder/order-sheet/blob/develop/mall/core/src/main/java/com/naver/fin/mall/core/paymethod/domain/enums/PayMethodType.java
 * @ref 참고 https://oss.fin.navercorp.com/payOrder/order-sheet/blob/develop/mall/core/src/main/java/com/naver/fin/mall/core/apiclient/rewardpoint/type/AccumulatePayMethodType.java
 * */
export const PayMethod = {
    BANK: 'BANK',
    /** 계좌 간편결제 */
    BANK_EASY_PAY: 'BANK_EASY_PAY',
    /** 신용카드 */
    CARD: 'CARD',
    /** 신용카드 간편결제 */
    CARD_EASY_PAY: 'CARD_EASY_PAY',
    /** 충전한 포인트 */
    CHARGE_POINT: 'CHARGE_POINT',
    /** 충전 결제 */
    RECHARGE_POINT: 'RECHARGE_POINT',
    /** 휴대폰 */
    MOBILE: 'MOBILE',
    /** 휴대폰 간편결제(?) */
    MOBILE_EASY_PAY: 'MOBILE_EASY_PAY',
    /** 네이버페이 포인트 */
    NAVERPAY_POINT: 'NAVERPAY_POINT',
    /** todo 확인 필요 */
    NONE: 'NONE',
    /** 적립된 포인트 */
    REWARD_POINT: 'REWARD_POINT',
    /** 문화누리카드 */
    WELFARE_CARD: 'WELFARE_CARD',
    /** 컬쳐랜드 */
    CULTURE_LAND: 'CULTURE_LAND',
    /** 해피머니 */
    HAPPY_MONEY: 'HAPPY_MONEY',
    /** 티머니 **/
    MONEY_TOKEN: 'MONEY_TOKEN',
    /** PayPal */
    PAY_PAL: 'PAY_PAL',
    /** 후불결제 */
    PAY_LATER: 'PAY_LATER',
    /** 나중에결제 */
    SKIP: 'SKIP',
    /** 무통장입금 */
    VIRTUAL_ACCOUNT: 'VIRTUAL_ACCOUNT',
    /** 삼성페이 */
    SAMSUNG_PAY: 'SAMSUNG_PAY',
    /** 애플페이 */
    APPLE_PAY: 'APPLE_PAY',
    /** 패밀리 결제 */
    FAMILY_PAY: 'FAMILY_PAY',
} as const

export type PayMethodType = Constant<typeof PayMethod>

export const PayMethodVoucher = {
    CULTURE_LAND: 'CULTURE_LAND',
    HAPPY_MONEY: 'HAPPY_MONEY',
}

export type PayMethodVoucherType = Constant<typeof PayMethodVoucher>

/** 선택 가능한 나중에결제 수단 */
export const PayNextMethod = {
    VIRTUAL_ACCOUNT: PayMethod.VIRTUAL_ACCOUNT,
} as const

/** 선택 가능한 나중에결제 수단 */
export type PayNextMethodType = Constant<typeof PayNextMethod>

/** 선택 가능한 주문형 결제 수단 */
export const OrderSheetPayMethods: PayMethodType[] = [
    PayMethod.BANK_EASY_PAY,
    PayMethod.CARD,
    PayMethod.CARD_EASY_PAY,
    PayMethod.CHARGE_POINT,
    PayMethod.RECHARGE_POINT,
    PayMethod.MOBILE,
    PayMethod.MOBILE_EASY_PAY,
    PayMethod.SKIP,
    PayMethod.VIRTUAL_ACCOUNT,
    PayMethod.SAMSUNG_PAY,
]

/** 주결제 수단에서 선택할 수 있는 Tab */
export const PaymentTabMethod = {
    /** 포인트 충전결제 */
    RECHARGE: 'RECHARGE',
    /** 간편카드결제 */
    EASY_CARD: 'EASY_CARD',
    /** 간편계좌결제 */
    EASY_ACCOUNT: 'EASY_ACCOUNT',
    /** 일반결제 */
    NORMAL: 'NORMAL',
    /** 삼성페이 */
    SAMSUNG_PAY: 'SAMSUNG_PAY',
    /** 패밀리결제 */
    FAMILY_PAY: 'FAMILY_PAY',
    /** 휴대폰 결제 */
    MOBILE: 'MOBILE',
    /** 선택안함 */
    NONE: 'NONE',
} as const

export type PaymentTabMethodType = Constant<typeof PaymentTabMethod>

/** 일반결제 수단에서 선택할 수 있는 Tab */
export const NormalTabMethod = {
    /** 신용카드 결제 */
    CARD: 'CARD',
    /** 휴대폰 결제 */
    MOBILE: 'MOBILE',
    /** 무통장 입금 */
    VIRTUAL_ACCOUNT: 'VIRTUAL_ACCOUNT',
    /** 문화누리카드 */
    WELFARE_CARD: 'WELFARE_CARD',
    /** 상품권 */
    VOUCHER: 'VOUCHER',
    /** T-money */
    MONEY_TOKEN: 'MONEY_TOKEN',
    /** PayPal */
    PAY_PAL: 'PAY_PAL',
} as const

export type NormalTabMethodType = Constant<typeof NormalTabMethod>

export const DetailCardTab = {
    UNION_PAY: 'UNION_PAY',
}

export type DetailCardTabType = Constant<typeof DetailCardTab>

/** 일반결제수단 탭선택 순서 */
export const NormalTabMethods: NormalTabMethodType[] = [
    NormalTabMethod.CARD,
    NormalTabMethod.MOBILE,
    NormalTabMethod.VOUCHER,
    NormalTabMethod.MONEY_TOKEN,
    NormalTabMethod.WELFARE_CARD,
    NormalTabMethod.PAY_PAL,
    NormalTabMethod.VIRTUAL_ACCOUNT,
]

/** 일반결제수단 선택 순서 */
export const NormalPayMethods: PayMethodType[] = [
    PayMethod.CARD,
    PayMethod.MOBILE,
    PayMethod.MONEY_TOKEN,
    PayMethod.WELFARE_CARD,
    PayMethod.CULTURE_LAND,
    PayMethod.HAPPY_MONEY,
    PayMethod.PAY_PAL,
    PayMethod.VIRTUAL_ACCOUNT,
]

/** 정기결제 (디컨) 결제수단 타입 */
export const SubscribePayMethod = {
    BANK_EASY_PAY: PayMethod.BANK_EASY_PAY,
    CARD_EASY_PAY: PayMethod.CARD_EASY_PAY,
    RECHARGE_POINT: PayMethod.RECHARGE_POINT,
    MOBILE: PayMethod.MOBILE,
    NONE: PayMethod.NONE,
} as const

export type SubscribePayMethodType = Constant<typeof SubscribePayMethod>

/** 주문 등록 대상자 */
export const OrderSheetRegister = {
    /** 상품 상세 */
    IMMEDIATE_ORDER: 'IMMEDIATE_ORDER',
    /** 장바구니 */
    CART_ORDER: 'CART_ORDER',
    /** 재구매 */
    REPURCHASE: 'REPURCHASE',
} as const

export type OrderSheetRegisterType = Constant<typeof OrderSheetRegister>

/** [TODO] common 하위로 이동 (24.01.04 - sbill 에서 사용중인 channel 때문에 이동 불가)
 * 주문서 진입점 타입 (payment, payment-methods에서 사용할 타입도 유니온으로 추가) */
export type EntryPointType = OrderSheetRegisterType

/**
 * 예블카 브릿지에서 넘어오는 SaleChannelType
 * @ref 참고 https://oss.fin.navercorp.com/payOrder/order-sheet/blob/develop/easybooking/core/src/main/java/com/naver/fin/easybooking/core/ordersheet/domain/enums/SaleChannelType.java
 **/
export const EasyBookingGroupSaleChannel = {
    /** 네이버 예약 */
    EASY_BOOKING: 'EASYBOOKING',
    /** 테이블오더(v1, v2) */
    LOCAL_PAY: 'LOCALPAY',
    /** 네이버 카페(v1) */
    NAVER_CAFE: 'NAVERCAFE',
    /** 네이버 블로그(v2) */
    NAVER_BLOG: 'NAVERBLOG',
    FLEAMARKET: 'FLEAMARKET',
} as const

export type EasyBookingGroupSaleChannelType = Constant<typeof EasyBookingGroupSaleChannel>

export const PayPointAmount = {
    /** @description 보유 가능한 최대 포인트 금액 */
    MAXIMUM_HOLDING_AMOUNT: 2_000_000,
    /** @description 충전 최소 금액. */
    MINIMUM_RECHARGE_AMOUNT: 10_000,
    /** 사용가능한 최대 포인트 금액 */
    MAXIMUM_POINT_BALANCE: 2_000_000,
    /** 월 최대 충전 가능한 금액 */
    MAXIMUM_MONTHLY_RECHARGE_AMOUNT: 10_000_000,
    /** 월 최대 충전 가능한 금액 (미성년자) */
    MAXIMUM_MONTHLY_RECHARGE_AMOUNT_BY_MINOR: 2_000_000,
} as const

// 은행 코드
export const BankCode = {
    /** 한국은행 */
    HANKOOK: '001',
    /** 산업은행 */
    KDB: '002',
    /** 기업은행 */
    IBK: '003',
    /** 국민은행 */
    KB_STAR: '004',
    // 외환은행 005 ( > 하나은행)
    /** 수협은행 (수협중앙회) */
    SUHYUP_BANK: '007',
    /** 수출입은행 */
    KOREA_EXIM: '008',
    // NH농협 010
    /** NH농협 (농협중앙회) */
    NONG_HYUB: '011',
    /** 농협회원조합 (지역농.축협) */
    LOCAL_NONG_HYUB: '012',
    /**
     * 단위농협 (013, 014, 015) (017, 018)
     * 축협 016 (NH 통합)
     */
    /** 우리은행 */
    WOORI_BANK: '020',
    // 신한은행 021 ( > 조흥 > 신한) 통합
    /** SC은행 (SC제일은행) */
    STANDARD_CHATERED: '023',
    /** 한국씨티은행 */
    CITI_BANK: '027',
    /** iM뱅크 (구 대구은행) */
    DGB: '031',
    /** 부산은행 */
    BUSAN_BANK: '032',
    /** 광주은행 */
    KJ_BANK: '034',
    /** 제주은행 */
    JEJU_BANK: '035',
    /** 전북은행 */
    JB_BANK: '037',
    // 강원은행 038 ( > 조흥 > 신한)
    /** 경남은행 */
    KB_BANK: '039',
    /** 새마을금고 (새마을금고연합회) */
    KFCC: '045',
    /** 신협 (신협중앙회) */
    CU: '048',
    /** 저축은행 */
    FSB: '050',
    /** 모건스탠리은행 */
    MS: '052',
    // 씨티은행 053
    /** HSBC은행 */
    HSBC: '054',
    /** 도이치은행 */
    DEUTSCHE: '055',
    // 에이비엔암로은행 056
    /** 제이피모간체이스은행 */
    JP_MORGAN: '057',
    /** 미즈호코퍼레이트은행 */
    MIZUHO: '058',
    /** 미쓰비시도쿄UFJ은행 */
    UFJ: '059',
    /** BOA */
    BOA: '060',
    /** 우체국 */
    EPOST_BANK: '071',
    /** 신용보증기금 */
    KODIT: '076',
    /** 기술신용보증기금 */
    TECHNOLOGY: '077',
    /** KEB하나은행 (하나은행) */
    HANA_BANK: '081',
    // 평화은행 083 ( > 한빛 은행 > 우리은행)
    /** 신한은행 */
    SHINHAN: '088',
    /** 케이뱅크 */
    K_BANK: '089',
    /** 카카오뱅크 */
    KAKAO: '090',
    /** 토스뱅크 */
    TOSS: '092',
    /** SBI 저축은행 */
    FSBSBISB: '103',
    /** 한국주택금융공사 */
    HF: '093',
    /** 서울보증보험 */
    SGIC: '094',
    /** 경찰청 */
    POLICE: '095',
    /** 금융결제원 */
    KFTC: '099',
} as const

export type BankCodeType = Constant<typeof BankCode>

// 금융투자회사 코드
export const FinancialInvestmentCompanyCode = {
    /** 유안타증권 */
    YUANTA: '209',
    /** KB증권 (현대증권) */
    KBSEC: '218',
    /** 미래에셋증권 */
    MIRAEASSET2: '230',
    /** 미래에셋대우 (미래에셋증권) */
    MIRAEASSET: '238',
    /** 삼성증권 */
    SAMSUNG: '240',
    /** 한국투자증권 */
    KOREA: '243',
    /** NH투자증권 (우리투자증권) */
    NHQV: '247',
    /** 교보증권 */
    KYOBO: '261',
    /** 하이투자증권 */
    HI: '262',
    /** 에이치엠씨투자증권 (현대차증권) */
    HMC: '263',
    /** 키움증권 */
    KIWOOM: '264',
    /** 이베스트투자 (이트레이드증권) */
    EBEST: '265',
    /** SK증권 */
    SK: '266',
    /** 대신증권 */
    DAESHIN: '267',
    /** 솔로몬투자증권 (메리츠증권) */
    SOLOMON: '268',
    /** 한화증권 */
    HANWHA: '269',
    /** 하나금융투자 */
    HANAW: '270',
    /** 신한투자증권 */
    SHINHANINVEST: '278',
    /** DB금융투자증권 */
    DB: '279',
    /** 유진투자증권 */
    EUGENEFN: '280',
    /** 메리츠종합금융증권 */
    MERITZ: '287',
    /** 신영증권 */
    SHINYOUNG: '291',
    /** 한국포스증권 */
    FOSS: '294',
    /** NH나무증권(모바일나무 증권) */
    NAMUH: '2470',
    /** 크레온증권 */
    CREON: '2670',
} as const
export type FinancialInvestmentCompanyCodeType = Constant<typeof FinancialInvestmentCompanyCode>

/**
 * Sheet Component 타입
 */
export const Sheet = {
    /** 충전 계좌 선택 Bottom Sheet */
    RECHARGE_ACCOUNT_LIST: 'RECHARGE_ACCOUNT_LIST',
    /** 환불 계좌 선택 Bottom Sheet */
    REFUND_ACCOUNT_LIST: 'REFUND_ACCOUNT_LIST',
    /** 충전 금액 입력 Top Sheet */
    RECHARGE_POINT_AMOUNT: 'RECHARGE_POINT_AMOUNT',
    /** 멤버십 가입 초대 Bottom Sheet */
    INVITE_TO_MEMBERSHIP: 'INVITE_TO_MEMBERSHIP',
    /** 네이버 통장 광고 Bottom Sheet */
    NAVER_BANKBOOK_AD: 'NAVER_BANKBOOK_AD',
    /** 멤버십 최초 가입 프로모션(주문완료) */
    MEMBERSHIP_FIRST_PROMOTION: 'MEMBERSHIP_FIRST_PROMOTION',
    /** 멤버십 최초 가입 프로모션(주문서) */
    MEMBERSHIP_ORDERSHEET_JOIN_PROMOTION: 'MEMBERSHIP_ORDERSHEET_JOIN_PROMOTION',
    /** 첫주문 간편결제수단 등록 Bottom Sheet */
    FIRST_EASY_PAYMENT_REGISTER: 'FIRST_EASY_PAYMENT_REGISTER',
    /** AML 이행유도 Sheet (주문완료) */
    AML_NOTICE_GUIDE: 'AML_NOTICE_GUIDE',
    /** 오픈뱅킹 동의 */
    CONSENT_OPEN_BANKING: 'CONSENT_OPEN_BANKING',
    /** 주문완료 > 디테일 배송메모 리스트 Sheet */
    DETAIL_DELIVERY_MEMO_LIST: 'DETAIL_DELIVERY_MEMO_LIST',
    /** 빈 컴포넌트 */
    DEFAULT: 'DEFAULT',
} as const

export type SheetType = Constant<typeof Sheet>

/** 세금 타입 */
export const Tax = {
    /** 과세 */
    TAXATION: 'TAXATION',
    /** 면세 */
    TAX_EXEMPTION: 'TAX_EXEMPTION',
    /** 영세 */
    TAX_FREE: 'TAX_FREE',
} as const

export type TaxType = Constant<typeof Tax>

/** 성인 인증 수단 */
export const CertificationMethod = {
    CELLPHONE: 'CELLPHONE',
    MOBILE: 'MOBILE',
    PC_PHONE: 'PC_PHONE',
    IPIN: 'IPIN',
} as const

export type CertificationMethodType = Constant<typeof CertificationMethod>

/** 최대 보유 포인트 잔액 */
export const MAXIMUM_POINT_BALANCE = 2_000_000

/** API Scene Status */
export const APIStatus = {
    IDLE: 'IDLE',
    PENDING: 'PENDING',
    FETCHING: 'FETCHING',
    RESOLVED: 'RESOLVED',
    REJECTED: 'REJECTED',
    DISABLED: 'DISABLED',
} as const

export type APIStatusType = Constant<typeof APIStatus>

/**
 * 대기열 상태
 * V1 시나리오
 * INITIALIZED(기본상태) -> TICKET_PENDING(대기열 진입여부 조회 중) -> TICKET_ISSUED(대기열 티켓 발급받음) -> TICKET_CHECK_PENDING(대기열 정보 조회 중)
 * > EXPIRIED(티켓 만료) case -> TICKET_PENDING 부터 재진행 (최대 1번 추가 재조회)
 * > EXPELLED(강제퇴장) case -> orderInterrupted 처리
 * > QUEUING(소켓 정상연결 or 폴링) case -> 소켓 정보조회 or polling 통해 ENTERABLE or EXPELLED or EXPIRED 처리
 * > ENTERABLE(대기열 입장 가능) case -> 주문서 진입
 *
 * V2 시나리오
 * INITIALIZED(기본상태) -> TICKET_PENDING(대기열 진입여부 조회 중) -> TICKET_ISSUED(대기열 티켓 발급받음)
 * > EXPIRIED(티켓 만료) case -> TICKET_PENDING 부터 재진행 (최대 1번 추가 재조회)
 * > EXPELLED(강제퇴장) case -> orderInterrupted 처리
 * > QUEUING(3회까지 SSE 연결시도) case -> ENTER_REQUESTING (enterable 이벤트 수신 시 주문서 진입요청)
 * > POLLING(SSE 연결 실패 시, polling은 5회 실패 시 orderInterrupted 처리) case -> ENTER_REQUESTING (enterable 이벤트 수신 시 주문서 진입요청)
 * > ENTERABLE(대기열 입장 가능) case -> 주문서 진입
 **/
export const WaitingStatus = {
    INITIALIZED: 'INITIALIZED',
    TICKET_PENDING: 'TICKET_PENDING',
    /** 대기열 티켓이 발급된 상태 */
    TICKET_ISSUED: 'TICKET_ISSUED',
    TICKET_CHECK_PENDING: 'TICKET_CHECK_PENDING',
    TICKET_CHECK_SUCCEED: 'TICKET_CHECK_SUCCEED',
    /** 대기열 티켓 발급 후 대기열 정보를 연속적으로 조회중인 상태 */
    QUEUING: 'QUEUING',
    /** 대기열 티켓 발급 후 SSE 조회에서 polling 으로 넘어간 상태 */
    POLLING: 'POLLING',
    /** 대기열 > 주문서 입장 요청중인 상태 */
    ENTER_REQUESTING: 'ENTER_REQUESTING',
    ENTERABLE: 'ENTERABLE',
    EXPELLED: 'EXPELLED',
    EXPIRED: 'EXPIRED',
    FAILED: 'FAILED',
} as const

export type WaitingStatusType = Constant<typeof WaitingStatus>

export const Device = {
    MOBILE: 'MOBILE',
    PC: 'PC',
} as const

export type DeviceType = Constant<typeof Device>

export const LockMethod = {
    /** 생체인증 */
    BIOMETRICS: 'BIOMETRICS',
    /** 패스워드, 패턴, 패스코드 등 */
    PASSWORD: 'PASSWORD',
    /** 잠금 사용하지 않음 */
    INSECURE: 'INSECURE',
} as const

export type LockMethodType = Constant<typeof LockMethod>

export const OS = {
    ANDROID: 'ANDROID',
    ETC: 'ETC',
    IOS: 'IOS',
    LINUX: 'LINUX',
    MAC_OS: 'MAC_OS',
    WINDOWS: 'WINDOWS',
} as const

export type OSType = Constant<typeof OS>

export const MobileApp = {
    NAVER: 'NAVER',
    NAVER_PAY: 'NAVER_PAY',
    NAVER_CAFE: 'NAVER_CAFE',
    /** 네이버 쇼핑 앱 */
    NAVER_SHOPPING: 'NAVER_SHOPPING',
    NAVER_WEBTOON: 'NAVER_WEBTOON',
    SERIES: 'SERIES',
    VIBE: 'VIBE',
    CHZZK: 'CHZZK',
    NAVER_MAP: 'NAVER_MAP',
    UNKNOWN: 'UNKNOWN',
} as const

export type MobileAppType = Constant<typeof MobileApp>

export const BrowserApp = {
    CHROME: 'CHROME',
    FIREFOX: 'FIREFOX',
    SAFARI: 'SAFARI',
    OPERA: 'OPERA',
    IE: 'IE',
    EDGE: 'EDGE',
    ETC: 'ETC',
} as const

export type BrowserAppType = Constant<typeof BrowserApp>

export const OrderProcessState = {
    /**  초기 상태 */
    NOT_STARTED: 'NOT_STARTED',
    /**  주문 시작한 상태 */
    STARTED: 'STARTED',
    /** order용 소켓 연결 완료 */
    ORDER_SOCKET_DONE: 'ORDER_SOCKET_DONE',
    /**  처리 대기 */
    PENDING_PROCESS: 'PENDING_PROCESS',
    /**  FDS 처리 완료 */
    FDS_AUTH_DONE: 'FDS_AUTH_DONE',
    /** apply용 소켓 연결 완료 */
    APPLY_SOCKET_DONE: 'APPLY_SOCKET_DONE',
    /** apply 시작한 상태 */
    APPLY_STARTED: 'APPLY_STARTED',

    /** API 명세상 값 */
    PREPARED: 'PREPARED',
    ORDER_PROCESS_ACCEPTED: 'ORDER_PROCESS_ACCEPTED',
    ORDER_PROCESS_VALIDATED: 'ORDER_PROCESS_VALIDATED',
    VALIDATE_ORDER_PROCESS_FAILED: 'VALIDATE_ORDER_PROCESS_FAILED',
    ORDER_STARTED: 'ORDER_STARTED',
    START_ORDER_FAILED: 'START_ORDER_FAILED',
    ORDER_APPLIED: 'ORDER_APPLIED',
    APPLY_ORDER_PROCESS_VALIDATED: 'APPLY_ORDER_PROCESS_VALIDATED',
    VALIDATE_APPLY_ORDER_PROCESS_FAILED: 'VALIDATE_APPLY_ORDER_PROCESS_FAILED',
    ORDER_PERSISTED: 'ORDER_PERSISTED',
    PERSIST_ORDER_FAILED: 'PERSIST_ORDER_FAILED',
    ORDER_PROCESS_CANCELED: 'ORDER_PROCESS_CANCELED',
    PAYMENT_REQUESTED: 'PAYMENT_REQUESTED',
    PAYMENT_REQUEST_FAILED: 'PAYMENT_REQUEST_FAILED',
    PAYMENT_COMPLETED: 'PAYMENT_COMPLETED',
    PAYMENT_FAILED: 'PAYMENT_FAILED',
    ROLLBACK_PAYMENT_STARTED: 'ROLLBACK_PAYMENT_STARTED',
    REWARD_POINT_RESERVED: 'REWARD_POINT_RESERVED',
    REWARD_POINT_RESERVE_FAILED: 'REWARD_POINT_RESERVE_FAILED',
    ORDER_INVALIDATED: 'ORDER_INVALIDATED',
    ORDER_COMPLETED: 'ORDER_COMPLETED',
    COMPLETE_ORDER_FAILED: 'COMPLETE_ORDER_FAILED',
    REDUCE_STOCK_FAILED: 'REDUCE_STOCK_FAILED',
    PERSIST_ORDER_REQUESTED: 'PERSIST_ORDER_REQUESTED',
    PERSIST_ORDER_REQUEST_FAILED: 'PERSIST_ORDER_REQUEST_FAILED',
    PAYMENT_ROLLBACK_COMPLETED: 'PAYMENT_ROLLBACK_COMPLETED',
    STOCK_REDUCED: 'STOCK_REDUCED',
    COMPLETE_ORDER_REQUESTED: 'COMPLETE_ORDER_REQUESTED',
    COMPLETE_ORDER_REQUEST_FAILED: 'COMPLETE_ORDER_REQUEST_FAILED',
    INVALIDATE_ORDER_FAILED: 'INVALIDATE_ORDER_FAILED',
    // order 폴링 타임아웃
    PREPARE_ORDER_PROCESS_TIMEOUT: 'PREPARE_ORDER_PROCESS_TIMEOUT',
    // apply 폴링 타임아웃
    APPLY_ORDER_PROCESS_TIMEOUT: 'APPLY_ORDER_PROCESS_TIMEOUT',
    REDUCE_PROMOTION_BUDGET_FAILED: 'REDUCE_PROMOTION_BUDGET_FAILED',
} as const

export type OrderProcessStateType = Constant<typeof OrderProcessState>

/** FE에서 처리해야하는 주문 상태 값들 */
export const OrderProcessStateSyncMap = [
    /** 성공 */
    OrderProcessState.ORDER_STARTED,
    OrderProcessState.ORDER_COMPLETED,
    /** 실패 */
    OrderProcessState.ORDER_INVALIDATED,
    // process 이벤트
    OrderProcessState.VALIDATE_ORDER_PROCESS_FAILED,
    OrderProcessState.START_ORDER_FAILED,
    OrderProcessState.PREPARE_ORDER_PROCESS_TIMEOUT,
    // apply 이벤트
    OrderProcessState.VALIDATE_APPLY_ORDER_PROCESS_FAILED,
    OrderProcessState.PERSIST_ORDER_FAILED,
    OrderProcessState.PERSIST_ORDER_REQUEST_FAILED,
    OrderProcessState.REDUCE_STOCK_FAILED,
    OrderProcessState.ORDER_PROCESS_CANCELED,
    OrderProcessState.APPLY_ORDER_PROCESS_TIMEOUT,
    // @see https://oss.fin.navercorp.com/payOrder/order-sheet/issues/16013#issuecomment-2179757
    OrderProcessState.PAYMENT_REQUEST_FAILED,
    OrderProcessState.PAYMENT_FAILED,
    OrderProcessState.REDUCE_PROMOTION_BUDGET_FAILED,
    OrderProcessState.REWARD_POINT_RESERVE_FAILED,
    OrderProcessState.COMPLETE_ORDER_REQUEST_FAILED,
    OrderProcessState.COMPLETE_ORDER_FAILED,
    OrderProcessState.INVALIDATE_ORDER_FAILED,
] as OrderProcessStateType[]

/** 나중에 무통장 입금 은행 코드 순서 */
export const VirtualAccountBankCodes = [
    BankCode.NONG_HYUB,
    BankCode.KB_STAR,
    BankCode.SHINHAN,
    BankCode.WOORI_BANK,
    BankCode.IBK,
    BankCode.HANA_BANK,
    BankCode.DGB,
    BankCode.BUSAN_BANK,
    BankCode.EPOST_BANK,
    BankCode.STANDARD_CHATERED,
    BankCode.KJ_BANK,
    BankCode.KB_BANK,
    BankCode.SUHYUP_BANK,
    BankCode.K_BANK,
]

// export const RoutesRegex = mapValues<typeof ROUTES.ORDER, RegExp | {[key: string]: RegExp}>(ROUTES.ORDER, (value) =>
//     typeof value === 'string' ? convertRoutesToRegex(value) : mapValues(value, convertRoutesToRegex),
// )

/** 일정 기간 노출여부를 제어하는 기능을 사용하는 곳에서 사용할 Key값 */
export const SkipDuringFewDateKey = {
    /** 충전결제 배너 */
    INDUCE_CHARGE_POINT_PAYMENT: 'INDUCE_CHARGE_POINT_PAYMENT',
    /** 나중에 계좌 결제 Dialog */
    GENERAL_SKIP_VIRTUAL_ACCOUNT: 'GENERAL_SKIP_VIRTUAL_ACCOUNT',
    /** 네이버 통장 소개 Bottom Sheet */
    NAVER_BANKBOOK_AD_BOTTOM_SHEET: 'NAVER_BANKBOOK_AD_BOTTOM_SHEET',
    /** 결제상세 광고배너 Bottom Sheet */
    PAYMENT_DETAIL_AD_BOTTOM_SHEET: 'PAYMENT_DETAIL_AD_BOTTOM_SHEET',
} as const

/** 결제승인 실패에 사용되는 은행 정보 [이름, [고객센터 번호]] */
export const BankInfo = {
    [BankCode.HANKOOK]: ['한국은행', []],
    [BankCode.KDB]: ['KDB산업은행', []],
    [BankCode.IBK]: ['기업은행', ['1566-2566, 1588-2588']],
    [BankCode.KB_STAR]: ['국민은행', ['1588-9999']],
    [BankCode.SUHYUP_BANK]: ['수협중앙회', ['1588-1515, 1644-1515']],
    [BankCode.KOREA_EXIM]: ['수출입은행', []],
    [BankCode.NONG_HYUB]: ['농협중앙회', ['1661-3000, 1522-3000']],
    [BankCode.LOCAL_NONG_HYUB]: ['농협회원조합', []],
    [BankCode.WOORI_BANK]: ['우리은행', ['1588-5000']],
    [BankCode.STANDARD_CHATERED]: ['SC제일은행', ['1588-1599']],
    [BankCode.CITI_BANK]: ['씨티은행', []],
    [BankCode.DGB]: ['iM뱅크', ['1566-5050']],
    [BankCode.BUSAN_BANK]: ['부산은행', ['1588-6200']],
    [BankCode.KJ_BANK]: ['광주은행', ['1600-4000']],
    [BankCode.JEJU_BANK]: ['제주은행', ['1588-0079']],
    [BankCode.JB_BANK]: ['전북은행', ['1588-4477']],
    [BankCode.KB_BANK]: ['경남은행', ['1600-8585']],
    [BankCode.KFCC]: ['새마을금고연합회', ['1599-9000, 1588-8801']],
    [BankCode.CU]: ['신협중앙회', []],
    [BankCode.FSB]: ['저축은행', []],
    [BankCode.MS]: ['모건스탠리은행', []],
    [BankCode.HSBC]: ['HSBC은행', []],
    [BankCode.DEUTSCHE]: ['도이치은행', []],
    [BankCode.JP_MORGAN]: ['제이피모간체이스은행', []],
    [BankCode.MIZUHO]: ['미즈호코퍼레이트은행', []],
    [BankCode.UFJ]: ['미쓰비시도쿄UFJ은행', []],
    [BankCode.BOA]: ['BOA', []],
    [BankCode.EPOST_BANK]: ['우체국', ['1599-1900, 1588-1900']],
    [BankCode.KODIT]: ['신용보증기금', []],
    [BankCode.TECHNOLOGY]: ['기술신용보증기금', []],
    [BankCode.HANA_BANK]: ['하나은행', ['1599-1111, 1588-1111']],
    [BankCode.SHINHAN]: ['신한은행', ['1577-8000']],
    [BankCode.K_BANK]: ['케이뱅크', []],
    [BankCode.KAKAO]: ['카카오뱅크', []],
    [BankCode.TOSS]: ['토스뱅크', []],
    [BankCode.HF]: ['한국주택금융공사', []],
    [BankCode.SGIC]: ['서울보증보험', []],
    [BankCode.POLICE]: ['경찰청', []],
    [BankCode.KFTC]: ['금융결제원', []],
    [BankCode.FSBSBISB]: ['SBI저축은행', []],
    [FinancialInvestmentCompanyCode.YUANTA]: ['유안타증권', []],
    [FinancialInvestmentCompanyCode.KBSEC]: ['KB증권', []],
    [FinancialInvestmentCompanyCode.MIRAEASSET2]: ['미래에셋증권', []],
    [FinancialInvestmentCompanyCode.MIRAEASSET]: ['미래에셋증권', []],
    [FinancialInvestmentCompanyCode.SAMSUNG]: ['삼성증권', []],
    [FinancialInvestmentCompanyCode.KOREA]: ['한국투자증권', []],
    [FinancialInvestmentCompanyCode.NHQV]: ['우리투자증권', []],
    [FinancialInvestmentCompanyCode.KYOBO]: ['교보증권', []],
    [FinancialInvestmentCompanyCode.HI]: ['하이투자증권', []],
    [FinancialInvestmentCompanyCode.HMC]: ['에이치엠씨투자증권', []],
    [FinancialInvestmentCompanyCode.KIWOOM]: ['키움증권', []],
    [FinancialInvestmentCompanyCode.EBEST]: ['이트레이드증권', []],
    [FinancialInvestmentCompanyCode.SK]: ['SK증권', []],
    [FinancialInvestmentCompanyCode.DAESHIN]: ['대신증권', []],
    [FinancialInvestmentCompanyCode.SOLOMON]: ['솔로몬투자증권', []],
    [FinancialInvestmentCompanyCode.HANWHA]: ['한화증권', []],
    [FinancialInvestmentCompanyCode.HANAW]: ['하나대투증권', []],
    [FinancialInvestmentCompanyCode.SHINHANINVEST]: ['신한투자증권', []],
    [FinancialInvestmentCompanyCode.DB]: ['동부증권', []],
    [FinancialInvestmentCompanyCode.EUGENEFN]: ['유진투자증권', []],
    [FinancialInvestmentCompanyCode.MERITZ]: ['메리츠증권', []],
    [FinancialInvestmentCompanyCode.SHINYOUNG]: ['신영증권', []],
    [FinancialInvestmentCompanyCode.NAMUH]: ['NH나무증권', []],
    [FinancialInvestmentCompanyCode.FOSS]: ['한국포스증권', []],
    [FinancialInvestmentCompanyCode.CREON]: ['크레온증권', []],
    '999': ['기타', []],
} as const

// 페이머니 응답코드 리턴 타입
export const PAYMONEY_RESULT_TYPE = {
    // 1000
    SUCCESS: 'SUCCESS',
    /**
     * code: 1000, 6007 그 외 모든 코드
     * 원천사 장애로 재시도 불가능한 상황
     */
    FAILED: 'FAILED',
    /**
     * code 6007
     * 기타 조회실패로 재조회가 가능한 실패
     */
    FAILED_RETRY_AVAILABLE: 'FAILED_RETRY_AVAILABLE',
} as const

export type PaymoneyResultType = Constant<typeof PAYMONEY_RESULT_TYPE>

export const ApiResult = {
    /** 성공 */
    SUCCESS: 'SUCCESS',
    /** 실패 */
    FAILED: 'FAILED',
    /** 재조회가 가능한 실패 */
    FAILED_RETRY_AVAILABLE: 'FAILED_RETRY_AVAILABLE',
} as const

export type ApiResultType = Constant<typeof ApiResult>

/** 주문서 ui 타입 */
export const CheckoutUI = {
    // 주문형 주문서
    ORDER: 'default',
    // 주문형 주문서
    FAMILY_PAY: 'familyPay',
    // 디지털 정기결제 주문서
    SUBSCRIPTION: 'subscription',
    // 디지털 단건결제 주문서
    DICON_BUYING: 'digitalContentBuy',
    // 포인트 적립대상이 아닌 주문서 (ex. 상품권 단건결제 주문서
    NO_POINT_ACCUMULATION: 'noPointAccumulationBuying',
    // 비실명 회원 주문서
    ANONYMOUS: 'anonymous',
    // 이외 타입
    NONE: 'NONE',
} as const

export const STI_INFO = {
    ORDER: {
        PC: 'pay',
        MO: 'm_pay',
        PC_WAITING: 'pay_waiting',
        MO_WAITING: 'm_pay_waiting',
        /** 장보기 */
        MO_BRANCH: 'm_pay_market',
        PC_BRANCH: 'pay_market',
        /** 선물하기 */
        MO_GIFTING: 'm_pay_gift',
        PC_GIFTING: 'pay_gift',
        PAY_APP: 'm_payapp',
        MO_NON_PURCHASABLE: 'm_pay_nonpurchasable',
    },
}

export type CheckoutUIType = Constant<typeof CheckoutUI>

/** 첫주문 Flow 유저 상태 */
export const OrderFlowUserLevel = {
    /** 1단계 */
    NO_DELIVERY: 'NO_DELIVERY',
    /** 2단계 */
    NO_PAYMENT: 'NO_PAYMENT',
    /** 3단계 */
    REGISTERED: 'REGISTERED',
} as const

export type OrderFlowUserLevelType = Constant<typeof OrderFlowUserLevel>

export const On = 'on'

export const Off = 'off'

export const MerchantChannelServiceType = {
    STOREFARM: '스마트스토어',
    SHOPPINGWINDOW: '쇼핑윈도',
} as const

export type MerchantChannelServiceTypeKey = keyof typeof MerchantChannelServiceType

export const ShoppingLiveStorageKey = 'shoppingLive'

export const UNAVAILABLE_REASON_TYPE = {
    DUPLICATION: 'DUPLICATION',
    /** 간편카드 사용불가 사유 */
    /** 선택된 프로모션 적용불가 카드 */
    INVALID_SELECTED_PROMOTION_CARD: 'INVALID_SELECTED_PROMOTION_CARD',
    /** 가맹점 지원불가 카드 */
    MERCHANT_NOT_SUPPORTING_CARD: 'MERCHANT_NOT_SUPPORTING_CARD',
    /** 원천사 점검 */
    CARD_COMPANY_UNDER_MAINTENANCE: 'CARD_COMPANY_UNDER_MAINTENANCE',
} as const

export type UnavailableReasonType = Constant<typeof UNAVAILABLE_REASON_TYPE>

export const PendingPaymentStatus = {
    ENABLE: 'ENABLE',
    UNABLE: 'UNABLE',
    DISABLE: 'DISABLE',
} as const

export type PendingPaymentStatusType = keyof typeof PendingPaymentStatus

export const ProductType = {
    NORMAL: 'NORMAL',
    BOOK: 'BOOK',
    TRADITIONAL_ALCOHOL: 'TRADITIONAL_ALCOHOL',
    ALCOHOL: 'ALCOHOL',
} as const

export type ProductTypeType = Constant<typeof ProductType>

/** 스토어 쿠폰 미적용 상품 타입 */
export const ExceptionProductTypesByStoreCoupon: ProductTypeType[] = [
    ProductType.TRADITIONAL_ALCOHOL,
    ProductType.BOOK,
    ProductType.ALCOHOL,
]

export const AccountPayInfo = {
    /** 동기 결제 가능 */
    PAY_AVAILABLE: 'PAY_AVAILABLE',
    /** 비동기 결제 가능 */
    PENDING_PAY_AVAILABLE: 'PENDING_PAY_AVAILABLE',
    /** 결제 불가 */
    PAY_UNABLE: 'PAY_UNABLE',
} as const

export type AccountPayInfoType = keyof typeof AccountPayInfo

export const ClosingMethod = {
    REDIRECT: 'REDIRECT',
    FORM_SUBMIT: 'FORM_SUBMIT',
    BACK: 'BACK',
    REPLACE: 'REPLACE',
    CLOSE: 'CLOSE',
} as const

/** 카드 상품 코드 */
export const CARD_PRODUCT_CODE = {
    WEBTOON_PLCC_CARD: ['AA19285', 'AA19286', 'AA19287', 'AA19433'],
}

export type ClosingMethodType = Constant<typeof ClosingMethod>

/** 닫기 시점 */
export const ClosingTiming = {
    /** 즉시 */
    IMMEDIATE: 'IMMEDIATE',
    /** 지연 */
    DELAYED: 'DELAYED',
} as const
export type ClosingTimingType = Constant<typeof ClosingTiming>

/**
 (1) 차등적립 배포 전 거래건
   - 네이버페이 머니 하나 통장 결제적립 2%
 (2) 차등적립 배포 후 거래건
   - 네이버페이 머니 결제적립 1.5%
   - 네이버페이 머니 하나통장 추가적립 0.5%
 * 추가적립과 결제적립은 다른 부분이니 주의
 */
/** 혜택 적립 유형 */
export const AccumulateClassType = {
    /** 디컨 기본적립 */
    DIGITAL_CONTENTS_ACM: 'DIGITAL_CONTENTS_ACM',
    /** 네이버페이 머니 하나 통장 결제적립 (차등적립 배포 전 거래건) */
    CHARGE_ADD_ACM: 'CHARGE_ADD_ACM',
    /** 네이버페이 머니 결제적립 (차등적립 배포 후 거래건) */
    PAY_MONEY_ADD_ACM: 'PAY_MONEY_ADD_ACM',
    /** 통장 추가적립 */
    BANKBOOK_ACM: 'BANKBOOK_ACM',
    /** @TODO 추후 주문형 적용시 각 key가 의미하는 적립명 파악하여 주석 작성 */
    BUY_COMPLETE: 'BUY_COMPLETE',
    REGISTER_USER: 'REGISTER_USER',
    EVENTEVENT: 'EVENT',
    WRITE_REVIEW: 'WRITE_REVIEW',
    WRITE_PREMIUM_REVIEW: 'WRITE_PREMIUM_REVIEW',
    AFTER_USE_WRITE_REVIEW: 'AFTER_USE_WRITE_REVIEW',
    AFTER_USE_WRITE_PREMIUM_REVIEW: 'AFTER_USE_WRITE_PREMIUM_REVIEW',
    NSP_BUY: 'NSP_BUY',
    BUY_COMPLETE_ADD_ACM: 'BUY_COMPLETE_ADD_ACM',
    ARRIVAL_GUARANTEE_ADD_ACM: 'ARRIVAL_GUARANTEE_ADD_ACM',
    MEMBERSHIP_ARRIVAL_GUARANTEE_ADD_ACM: 'MEMBERSHIP_ARRIVAL_GUARANTEE_ADD_ACM',
    PRE_ACM: 'PRE_ACM',
} as const
export type AccumulateClassTypeType = Constant<typeof AccumulateClassType>

export const BadgeVariant = {
    PRIMARY: 'PRIMARY',
    SECONDARY: 'SECONDARY',
    TERTIARY: 'TERTIARY',
    BASIC: 'BASIC',
    BASIC_FILL: 'BASIC-FILL',
} as const

export type BadgeVariantType = Constant<typeof BadgeVariant>

export const BadgeColor = {
    GREEN: 'GREEN',
    GRAY: 'GRAY',
    PURPLE: 'PURPLE',
    RED: 'RED',
    BLUE: 'BLUE',
    GRADIENT: 'GRADIENT',
    GRADIENT_BLUE: 'GRADIENT-BLUE',
    NPAY_GREEN: 'NPAY-GREEN',
} as const

export type BadgeColorType = Constant<typeof BadgeColor>

export const CPID = {
    /** 해피빈 */
    HAPPYBEAN: 'hbean',
    /** 쿠키 자동결제 */
    COOKIE: 'autocookie',
    /** 바이브 */
    VIBE: 'NVMUS',
    /** 검색결제 */
    SEARCH_AD: 'searchad',
    /** N드라이브 */
    NDRIVE: 'ndrive',
    /** 멤버십 */
    MEMBERSHIP: 'membership',
}

export const ChildInfo = {
    // 성인
    ADULT: 'ADULT',
    // 미성년자
    MINOR: 'MINOR',
    // 14세 미만
    CHILD: 'CHILD',
    // 생일 정보를 알수 없음
    UNKNOWN: 'UNKNOWN',
} as const

export type ChildInfoType = Constant<typeof ChildInfo>

// api관련
export * from './api'

// 동의 영역 관련
export * from './agreement'

// 배너 관련
export * from './banner'

// 쿠폰 관련
export * from './coupon'

// 배송 관련
export * from './delivery'

// 배송지변경창 관련
export * from './deliveryManager'

// 다이얼로그 아이디 관련
export * from './dialogId'

// 에러
export * from './error'

// GLAD
export * from './glad'

// 주문실패
export * from './orderFailure'

// 후불결제
export * from './payLater'

// 결제
export * from './payment'

// 전화번호
export * from './phone'

// 팝업
export * from './popup'

// 제품
export * from './product'

// SBill
export * from './sbill'

// 제휴포인트
export * from './affiliatePoint'

// url query string key
export * from './queryString'

// HTML element 요소 ID */
export * from './elementId'

export * from './case'

export * from './text'

export * from './scrollType'

// 페이지 이동 관련
export * from './pageMove'

export * from './fds'

export {AccumulationPointClassType} from './point'
// common slice 관련 constant
export * from './common'

export type {ScheduledOrderEntryType, OrderEntryType, RentalClassType} from './common'

export * from './number'

export * from './routers'

export * from './storage'

export * from './priorityPaymentMethod'

export * from './register'

export * from './point'
// 패밀리결제 관련
export * from './familyPay'

export * from './reward'

export * from './assignment'

export * from './subscription'

export * from './merchant'

// 썸네일 기본 이미지 관련
export type {DefaultThumbnailType} from './image'

export {DefaultThumbnail} from './image'

export * from './modal'

export * from './subscribe'

export * from './sale'

export * from './pointPromotion'

export * from './npayBiz'
